// Inline cross-link rendered on historical (renewed-but-expired) insurance
// rows. Surfaces "↳ renewed by {successor.name}" so admins can jump from
// the audit-only original to its in-force successor. Click navigates by
// setting ?item={successor.id} on the current URL while preserving every
// other search-param so the user's tab/filter context survives.

import type { MouseEvent } from "react";
import { useSearchParams } from "react-router";

export interface RenewalReferenceProps {
  successorId: string;
  successorName: string;
}

export default function RenewalReference({
  successorId,
  successorName,
}: RenewalReferenceProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Build the next href so the link is meaningful even without JS — middle
  // click / open-in-new-tab still works.
  const nextParams = new URLSearchParams(searchParams);
  nextParams.set("item", successorId);
  const href = `?${nextParams.toString()}`;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Allow the browser to honour modifier-clicks (new tab, etc.).
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    next.set("item", successorId);
    setSearchParams(next);
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      aria-label={`Renewed by ${successorName}`}
      className="inline-flex items-center gap-1 text-caption text-brand-600 hover:text-brand-500 hover:underline dark:text-brand-400"
    >
      <span aria-hidden="true">↳</span>
      <span>renewed by {successorName}</span>
    </a>
  );
}
